import React, { useCallback, useState, useEffect } from 'react';
import { Table, Space, Tooltip, Row, Col, Spin } from 'antd';
import { 
  TableWrapper, 
  ProjectEditModal, 
  ProjectScheduleEditModal, 
  ProjectDiagnoseEditModal, 
  BillB2BRequestModal, 
  PaymentRequestModal,
  ProjectMemoModal } from 'components/molecules';
import { usePopupManager } from 'react-popup-manager';
import { ColumnsType } from 'antd/lib/table/interface';
import { TotalNumMenu } from 'components/atoms';
import { addNewSearch } from 'utils/utils';
import { PlusSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';
import { useTableInfo } from 'hooks';
import { INIT_PAGESIZE } from 'commons/constants';
import { PROJECT_WORK_TYPE } from 'commons/constants';
import { useLocation, useNavigate } from 'react-router';
import { fetcher } from 'services';
import qs from 'query-string';
import * as XLSX from 'xlsx';

interface FormItemProps {
  projectList: ProjectListDto[] | undefined;  
  searchResult: any;
  hiddenColumns: string[];
  pType: PROJECT_WORK_TYPE;
  refetch: () => void;
}

const MYB2BTable = ({projectList, searchResult, hiddenColumns, pType, refetch}: FormItemProps): React.ReactElement => {
  /* ======================================================
   * variables
   * ====================================================== */
  const navigate = useNavigate();
  const location = useLocation();
  const { open } = usePopupManager();
  const [rowExpaned, setRowExpaned] = useState<number[]>([]);  
  const [subRowExpaned, setSubRowExpaned] = useState<string[]>([]); 
  const searchParams = qs.parse(location.search);
  const [isLoding, setIsLoading] = useState<boolean>(false);  

  const onCellClick = (record) => {
    return {
      onClick: (e) => {
        open(ProjectEditModal, {id: Number(record.id), onClose: (isOk)=>{if(isOk) {refetch();}}, pType:pType});         
      },
    };
   };

  const columns: ColumnsType<ProjectListDto> = [
    {
      title: '번호',
      dataIndex: 'id',
      key: 'id',
      width: '70px',
      align: 'center',
      fixed: 'left',
      onCell: (record) => {
        return {
            onClick: (e) => {
              if (rowExpaned.includes(record.id)) {
                setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
              } else {
                setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
              }              
            },
        };
      }, 
    },  
    {
      title: '상태',
      dataIndex: 'project_step',
      key: 'project_step',
      width: '80px',
      align: 'center', 
      ellipsis: true, 
      fixed: 'left',
      onCell: onCellClick,
      render: (project_step, record) => {
        return (
          <span>{project_step==='B'?'일정논의':'진행협의'}</span> 
        )
      }            
    }, 
    {
      title: '고객사',
      dataIndex: 'company_name',
      key: 'company_name',
      width: '150px',
      align: 'left', 
      ellipsis: true,  
      fixed: 'left',  
      onCell: onCellClick,      
    },  
    {
      title: '강의구분',
      dataIndex: 'lecture_type',
      key: 'lecture_type',
      width: '120px',
      align: 'center',
      ellipsis: true,
      onCell: onCellClick,
    },
    {
     title: '대상자',
     dataIndex: 'target',
     key: 'target',
     width: '120px',
     align: 'center',
     ellipsis: true,
     onCell: onCellClick,
   },   
   {
     title: '기간',
     dataIndex: 'project_date',
     key: 'project_date',
     width: '160px',
     align: 'center',
     ellipsis: true,
     onCell: onCellClick,
   },  
   {
    title: '강의주제',
    dataIndex: 'lecture_subject',
    key: 'lecture_subject',
    width: '120px',
    align: 'center',
    ellipsis: true,
    onCell: onCellClick,
  },      
    {
     title: '프로젝트명',
     dataIndex: 'title',
     key: 'title',
     width: 'auto',
     align: 'left',
     ellipsis: true,
     onCell: onCellClick,
   },   
    {
     title: '미발행매출',
     key: 'not_publish_pirce',
     dataIndex: 'not_publish_pirce',
     width: '100px',
     align: 'right',
     ellipsis: true,
     onCell: (record) => {
      return {
          onClick: (e) => {
            if (record.not_publish_pirce > 0) onBillRequestPopup(record.id);            
          },
      };
    }, 
     render: (not_publish_pirce, record) => {
      return (
        <span style={not_publish_pirce>0?{color:'#1890ff'}:{}}>{not_publish_pirce.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
      )
    }    
   },
   {
     title: '매출금액',
     key: 'max_price',
     dataIndex: 'max_price',
     width: '100px',
     align: 'right',
     render: (max_price, record) => {
      return (
        <span style={{color:max_price !== record.calc_price?'red':'black'}}>{max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span> 
      )
    }      
   },   
    {
      title: '입력매출',
      key: 'calc_price',
      dataIndex: 'calc_price',
      width: '80px',
      align: 'right',
      render: (calc_price, record) => {
       return (
        <span>{calc_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
       )
     }   
    },
    {
      title: '스케줄',
      key: 'schedule',
      width: '80px',
      align: 'center',
      render: (record) => {
        return (
          <>
            <a onClick={(e) => {
              if (rowExpaned.includes(record.id)) {
                setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
              } else {
                setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
              }
            }}>{record.schedule_count}건</a>&nbsp;|&nbsp;
            <a onClick={(e) => {
              onScheduleEditPopup(record.id, 0);
            }}>등록</a>
          </>
        )
      }
    } , 
    {
      title: '진단',
      key: 'diagnose',
      width: '80px',
      align: 'center',
      render: (record) => {
        return (
          <>
            <a onClick={(e) => {
              if (rowExpaned.includes(record.id)) {
                setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
              } else {
                setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
              }
            }}>{(record.diagnose)?record.diagnose.length:'0'}건</a>&nbsp;|&nbsp;
            <a onClick={(e) => {
              onDiagnoseEditPopup(record.id, 0);
            }}>등록</a>
          </>
        )
      }
    },    
    {
     title: '미지급강사비',
     key: 'calc_out_lecturer_price',
     dataIndex: 'calc_out_lecturer_price',
     width: '90px',
     align: 'right',
     onCell: (record) => {
      return {
          onClick: (e) => {
            if (record.calc_out_lecturer_price > 0) onPaymentRequestPopup(record.id);          
          },
      };
    },        
     render: (calc_out_lecturer_price, record) => {
      return (
        <span style={calc_out_lecturer_price>0?{color:'#1890ff'}:{}}>{calc_out_lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
      )
    }   
   },
   {
    title: '참고사항',
    key: 'history',
    dataIndex: 'history',
    width: '80px',
    align: 'center',
    render: (history, record) => {
     return (
       <>
         <a onClick={(e) => {
           e.stopPropagation();
           onMemoPopup(record.id);
         }}>{history}건</a>
       </>
     )
   }   
  },
  ];

  /* ======================================================
  * events
  * ====================================================== */ 
  const onMemoPopup = (group_id: number) => {
    open(ProjectMemoModal, {
      project_group_srl: group_id,
      onClose: (isOk) => {
        if (isOk) {
          refetch();
        }
      },
    }); 
   }

 const onClickRegister = () => {
    open(ProjectEditModal, {id: 0, onClose: (isOk)=>{if(isOk) {refetch();}}, pType:pType}); 
 }

const onClickSubRowExpaned = (id:string) => {
  if (subRowExpaned.indexOf(id)>-1) {
    setSubRowExpaned(subRowExpaned.filter(item => item !== id) ) 
  } else {
    setSubRowExpaned(preRowExpaned => [...preRowExpaned, id])
  }
}


 const onChangePageInfo = (pageNo: number, pageSize: number) => {
   navigate({
     ...location,
     search: addNewSearch(location.search, { pageNo, pageSize }),
   });
 };  

 const onBillRequestPopup = (id: number) => {
  open(BillB2BRequestModal, {
    id: id,
    onClose: (isOk) => {
      if (isOk) {
        refetch();
      }
    },
  }); 
 }

 const onPaymentRequestPopup = (id: number) => {
  open(PaymentRequestModal, {
    id: id,
    group: 'b2b',
    onClose: (isOk) => {
      if (isOk) {
        refetch();
      }
    },
  }); 
 }   

 const onScheduleEditPopup = (group_id: number, id: number) => {
  open(ProjectScheduleEditModal, {
    group_id: group_id,
    id: id,
    onClose: (isOk) => {
      if (isOk) {
        refetch();
      }
    },
  }); 
 }

 const onExcelDownload = () => {
    setIsLoading(true); 
    fetcher.project.postProjectListExcel({
      pageNo: 1,
      pageSize: 0,
      project_step: (pType==='confirm')?"'A'":(pType==='consult')?"'B','C'":"'D'",
      searchValue: searchParams.search ? String(searchParams.search) : '',  
      searchDateStart: searchParams.searchDateStart ? String(searchParams.searchDateStart) : '',   
      searchDateEnd: searchParams.searchDateEnd ? String(searchParams.searchDateEnd) : '',  
      biz: searchParams.biz ? String(searchParams.biz) : '',  
      pm: searchParams.pm ? String(searchParams.pm) : '',  
      cm: searchParams.cm ? String(searchParams.cm) : '',  
      target: searchParams.target ? String(searchParams.target) : '',  
      lecturer: searchParams.lecturer ? String(searchParams.lecturer) : '',  
      status: searchParams.status ? String(searchParams.status) : '',  
      lecture_type: searchParams.lecture_type ? String(searchParams.lecture_type) : '',  
      lecture_subject: searchParams.lecture_subject ? String(searchParams.lecture_subject) : '',  
      crm: searchParams.crm ? String(searchParams.crm) : '',  
      company: searchParams.company ? String(searchParams.company) : '',  
    })
    .then((res) => {
        switch (res.code) {
            case 200:

            const book = XLSX.utils.book_new();
    
            if (pType==='confirm') {
              const nurses = XLSX.utils.json_to_sheet(
                res.result.list.map((item, index) => {
                  return {
                    '번호' : item.project_id,
                    '고객사' : item.project_company_name,
                    '프로젝트명': item.project_title,
                    '강의구분': item.schedule_lecture_type,
                    '강의주제': item.schedule_lecture_subject,
                    '교육대상': item.schedule_target,
                    '교육일자': item.schedule_date,
                    '강의 시간': item.schedule_time,
                    '강사': item.schedule_lecturer,
                    '진단대상': item.diagnose_target,
                    '진단기간': item.diagnose_date,
                    '진단비용': item.diagnose_price,
                    '디브리핑비용': item.debriefing_price,                    
                    'PM': item.schedule_pm,
                    'CM': item.schedule_cm,
                    '강사매출': item.schedule_lecturer_price,
                    '교재비매출': item.schedule_teaching_price,
                    '교통비매출': item.schedule_traffic_price,
                    '설계비매출': item.schedule_plan_price,
                    '운영비매출': item.schedule_operating_price,
                    'HSG진단매출': item.schedule_diagnose_price,
                    'HSG컨설팅매출': item.schedule_consulting_price,
                    '스튜디오비매출': item.schedule_studio_price,
                    '기타비매출': item.schedule_etc_price,
                    '외주비': item.schedule_outside_order_price,
                  }
                }), 
                { 
                  header : [
                    '번호',
                    '고객사',
                    '프로젝트명',
                    '강의구분',
                    '강의주제',
                    '교육대상',
                    '교육일자',
                    '강의 시간',
                    '강사',
                    '진단대상',
                    '진단기간',
                    '진단비용',
                    '디브리핑비용',                      
                    'PM',
                    'CM',
                    '강사매출',
                    '교재비매출',
                    '교통비매출',
                    '설계비매출',
                    '운영비매출',
                    'HSG진단매출',
                    'HSG컨설팅매출',
                    '스튜디오비매출',
                    '기타비매출',
                    '외주비',
                  ], 
                  skipHeader : false 
                } 
              );
          
              nurses["!cols"] = [
                { wpx : 60 } 
              , { wpx : 120 } 
              , { wpx : 240 }  
              , { wpx : 120 }  
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 150 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              ]
          
              XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
          
              XLSX.writeFile( book, "b2b_확정.xlsx" ); 
            } else if (pType==='consult') {
              const nurses = XLSX.utils.json_to_sheet(
                res.result.list.map((item, index) => {
                  return {
                    '번호' : item.project_id,
                    '상태' : item.project_step==='B'?'일정논의':'진행협의',
                    '고객사' : item.project_company_name,
                    '프로젝트명': item.project_title,
                    '강의구분': item.schedule_lecture_type,
                    '강의주제': item.schedule_lecture_subject,
                    '교육대상': item.schedule_target,
                    '교육일자': item.schedule_date,
                    '강의 시간': item.schedule_time,
                    '강사': item.schedule_lecturer,
                    '진단대상': item.diagnose_target,
                    '진단기간': item.diagnose_date,
                    '진단비용': item.diagnose_price,
                    '디브리핑비용': item.debriefing_price,   
                    'PM': item.schedule_pm,
                    'CM': item.schedule_cm,
                    '강사매출': item.schedule_lecturer_price,
                    '교재비매출': item.schedule_teaching_price,
                    '교통비매출': item.schedule_traffic_price,
                    '설계비매출': item.schedule_plan_price,
                    '운영비매출': item.schedule_operating_price,
                    'HSG진단매출': item.schedule_diagnose_price,
                    'HSG컨설팅매출': item.schedule_consulting_price,
                    '스튜디오비매출': item.schedule_studio_price,
                    '기타비매출': item.schedule_etc_price,
                    '외주비': item.schedule_outside_order_price,
                  }
                }), 
                { 
                  header : [
                    '번호',
                    '상태',
                    '고객사',
                    '프로젝트명',
                    '강의구분',
                    '강의주제',
                    '교육대상',
                    '교육일자',
                    '강의 시간',
                    '강사',
                    '진단대상',
                    '진단기간',
                    '진단비용',
                    '디브리핑비용',                       
                    'PM',
                    'CM',
                    '강사매출',
                    '교재비매출',
                    '교통비매출',
                    '설계비매출',
                    '운영비매출',
                    'HSG진단매출',
                    'HSG컨설팅매출',
                    '스튜디오비매출',
                    '기타비매출',
                    '외주비',
                  ], 
                  skipHeader : false 
                } 
              );
          
              nurses["!cols"] = [
                { wpx : 60 } 
              , { wpx : 100 } 
              , { wpx : 120 }
              , { wpx : 240 }  
              , { wpx : 120 }  
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 150 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              ]
          
              XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
          
              XLSX.writeFile( book, "b2b_논의중.xlsx" ); 
            } else {
              const nurses = XLSX.utils.json_to_sheet(
                res.result.list.map((item, index) => {
                  return {
                    '번호' : item.project_id,
                    '고객사' : item.project_company_name,
                    '프로젝트명': item.project_title,
                    '강의구분': item.project_lecture_type,
                    '교육대상': item.project_target,                    
                    '기간': item.project_date, 
                    '강의주제': item.project_lecture_subject,
                    '매출금액': item.project_max_price,
                    'PM': item.project_pm,
                    '드롭사유': item.project_drop_reason,
                  }
                }), 
                { 
                  header : [
                    '번호',
                    '고객사',
                    '프로젝트명',
                    '강의구분',
                    '교육대상',                    
                    '기간',
                    '강의주제',
                    '매출금액',
                    'PM',
                    '드롭사유',
                  ], 
                  skipHeader : false 
                } 
              );
          
              nurses["!cols"] = [
                { wpx : 60 } 
              , { wpx : 100 } 
              , { wpx : 240 }  
              , { wpx : 120 }  
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 } 
              , { wpx : 120 }
              ]
          
              XLSX.utils.book_append_sheet( book, nurses, "NURSES" );
          
              XLSX.writeFile( book, "b2b_드롭.xlsx" ); 
            }

            break;
            default:  
            break;
        };
        setIsLoading(false);         
    }); 
 }  
 
 const onDiagnoseEditPopup = (group_id: number, id: number) => {
  open(ProjectDiagnoseEditModal, {
    group_id: group_id,
    id: id,
    pType: pType,
    onClose: (isOk) => {
      if (isOk) {
        refetch();
      }
    },
  }); 
} 
  /* ======================================================
  * hooks
  * ====================================================== */
  const { pagination, onTableChange } = useTableInfo({
    pageInfo: {
      pageNo: Number(searchParams.pageNo) || 1,
      pageSize: Number(searchParams.pageSize) || INIT_PAGESIZE,
    },
    datas: projectList,
    totalCount: Number(searchResult.totalCount),    
    onChangePageInfo,
  });

  useEffect(() => {
    setRowExpaned([]);
    setSubRowExpaned([]);
  }, [searchResult.pageNo]); 

  const onExpaned = (val: boolean) => {
    if (val) {
      if (!!projectList) {
        const rows: any[] = [];
        const subrows: any[] = [];
  
        projectList.forEach(record => {
          rows.push(record.id);
          record.monthly.forEach((monthly)=>{
            subrows.push(record.id+'_'+monthly.month);
          });
        })
  
        setRowExpaned(rows);
        setSubRowExpaned(subrows);      
      }
    } else {
      setRowExpaned([]);
      setSubRowExpaned([]);      
    }
  };

  /* ======================================================
   * render items
   * ====================================================== */
  return (
    <Space direction="vertical" style={{ width: '100%'}}>
      <Spin spinning={isLoding} tip="처리중입니다. 잠시만 기다려 주세요." size="large">
        <TotalNumMenu onExpaned={onExpaned} totalNum={searchResult.totalCount} useRegister useExcelDownload onUseRegister={onClickRegister} onDownloadExcel={onExcelDownload}/>
        <TableWrapper isSearch={!!searchParams.search || !!searchParams.lecturer_type || !!searchParams.lecturer_category}>
          <Table
            tableLayout="fixed"
            dataSource={projectList}
            columns={columns.filter((item) => hiddenColumns.findIndex((col)=>col === item.key) < 0)}
            pagination={pagination}
            onChange={onTableChange}
            style={{ width: '100%', overflowX: 'auto', minWidth: 600 }}
            className="columnCenterFixTable clickable"
            showSorterTooltip={false}
            rowKey={'id'}
            scroll={{ x: 1450 }}
            expandable={{
              expandedRowRender: (record) => (
                <Space direction="vertical" style={{padding:"5px 30px", width:"100%"}} size={3}>
                {record.monthly.map((monthly)=>(                    
                  <div key={record.id+'_'+monthly.month}>
                  {(subRowExpaned.indexOf(record.id+'_'+monthly.month) > -1)?(
                    <>
                      <div style={{display:"flex", alignItems:"center"}}>
                      <span style={{cursor:'pointer'}} onClick={() => onClickSubRowExpaned(record.id+'_'+monthly.month)}><MinusSquareOutlined/>&nbsp;{monthly.month} {monthly.schedule.length}건 {monthly.max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                      </div>
                      { (monthly.schedule.length > 0) && (
                        <div key={monthly.month} style={{backgroundColor:"#f3f3f3"}}>
                          <hr style={{color:"#eeeeee"}}/>
                          {monthly.schedule.map((schedule)=>( 
                            <Row key={schedule.id} style={{padding:"0px 15px", width:"100%"}}>
                              <Col span={3}><a onClick={()=> onScheduleEditPopup(record.id, schedule.id)}>{schedule.date} {schedule.time}</a></Col>
                              <Col span={3}>{schedule.lecture_type}</Col>
                              <Col span={2}>{schedule.lecture_place_type}</Col>
                              <Col span={1}>{schedule.lecturer}</Col>
                              <Col span={3}>{schedule.lecture_subject}</Col>
                              <Col span={2}>{schedule.pm}</Col>
                              <Col span={3}>{schedule.lecturer_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Col>
                              <Col span={3}>{schedule.etc_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Col>
                              <Col span={3} style={{textOverflow:"ellipsis",whiteSpace:"nowrap",overflow:"hidden"}}><Tooltip placement="topLeft" title={schedule.memo}>{schedule.memo}</Tooltip></Col>
                            </Row>
                          ))}
                          <hr style={{color:"#eeeeee"}} />
                        </div>
                      )}                      
                    </>
                  ) : (
                    <div style={{display:"flex", alignItems:"center"}}>
                      <span style={{cursor:'pointer'}} onClick={() => onClickSubRowExpaned(record.id+'_'+monthly.month)}><PlusSquareOutlined/>&nbsp;{monthly.month} {monthly.schedule.length}건 {monthly.max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                    </div>
                  )}
                  </div>
                ))}
                {record.diagnose && record.diagnose.map((diagnose)=>(                    
                    <div key={'D'+diagnose.id}>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                      <a onClick={()=> onDiagnoseEditPopup(record.id, diagnose.id)}>• [진단] {diagnose.diagnose_start_date} ~ {diagnose.study_end_date !== '미정'?diagnose.study_end_date:diagnose.diagnose_end_date} ({diagnose.target} {diagnose.people_count}명) - {diagnose.max_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</a>
                    </div>
                    <div style={{backgroundColor:"#f3f3f3"}}>
                      <hr style={{color:"#eeeeee"}}/>
                      <Row style={{padding:"5px"}}>
                        <Col flex="100px" style={{display:"flex", justifyContent:"center"}}>
                          진단
                        </Col>
                        <Col>
                          {diagnose.diagnose_start_date} ~ {diagnose.diagnose_end_date} - 비용 : {diagnose.diagnose_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </Col>
                      </Row>         
                      <Row style={{padding:"5px"}}>
                        <Col flex="100px" style={{display:"flex", justifyContent:"center"}}>
                          디브리핑
                        </Col>
                        <Col>
                        {diagnose.debriefing_date}&nbsp;&nbsp;{diagnose.debriefing_start_time} ~ {diagnose.debriefing_end_time}&nbsp;&nbsp;{diagnose.debriefing_lecturer} - 비용 : {diagnose.debriefing_price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </Col>
                      </Row>  
                      <Row style={{padding:"5px"}}>
                        <Col flex="100px" style={{display:"flex", justifyContent:"center"}}>
                          영상학습
                        </Col>
                        <Col>
                          {diagnose.study_start_date} ~ {diagnose.study_end_date}
                        </Col>
                      </Row>                                                             
                      <hr style={{color:"#eeeeee"}} />
                    </div>   
                    </div>
                  ))}                  
                </Space>
              ),
              columnWidth: 25,
              expandedRowKeys: rowExpaned,
              onExpand: (expaned, record) => { 
                if (expaned) {
                  setRowExpaned(preRowExpaned => [...preRowExpaned, record.id])
                } else {
                  setRowExpaned(rowExpaned.filter(item => item !== record.id) ) 
                }
              },                
            }}
          />
        </TableWrapper> 
      </Spin>
    </Space>
  );
};

export default MYB2BTable;
